import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  alpha,
  Badge,
  Alert,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import TableItem from "../../../components/TableItem";
import BillItemDetailsDialog from "../../../components/BillItemDetailsDialog";
import {
  getWarehouseTransferBillById,
  sendWarehouseTransferBillForApproval,
  approveWarehouseTransferBill,
  rejectWarehouseTransferBill,
  executeWarehouseTransfer,
  confirmWarehouseTransferReceived,
} from "../../../services/WarehouseTransfer";
import {
  getSaleBillPaymentsById,
  updateSaleBillPayments,
} from "../../../services/Sales";
import CloseIcon from "@mui/icons-material/Close";
import WarehouseTransferBillWarehouseView from "../../../components/WarehouseTransferBillWarehouseView";
import PaymentOptions from "../../../components/PaymentOptions";
import PaymentListForConfirmation from "../../../components/PaymentListForConfimation";

// Icons
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DescriptionIcon from "@mui/icons-material/Description";
import DiscountIcon from "@mui/icons-material/Discount";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import TagIcon from "@mui/icons-material/Tag";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

function WarehouseTransferBillView() {
  const theme = useTheme();
  const history = useHistory();
  const { billId } = useParams();
  const [bill, setBill] = useState(null);
  const [rows, setRows] = useState([]);
  const [transferItems, setTransferItems] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [displayAlert, setDisplayAlert] = useState(false);
  const [alertData, setAlertData] = useState({ type: "", message: "" });
  const [openItemView, setOpenItemView] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [existingPayments, setExistingPayments] = useState([]);
  const [newPayments, setNewPayments] = useState([]);
  const [enablePayment, setEnablePayment] = useState(false);
  const [paymentAlert, setPaymentAlert] = useState({
    show: false,
    type: "",
    message: "",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [result, paymentsResult] = await Promise.all([
        getWarehouseTransferBillById(billId),
        getSaleBillPaymentsById(billId),
      ]);
      if (result && result !== "Something went wrong...") {
        setBill(result);
        const rawItems = result.transferItems || [];
        setTransferItems(rawItems);
        const itemRows = rawItems.map((item) => ({
          productName: item.productName,
          barcode: item.barcode,
          sellingQty: item.billQty,
          unitPrice: item.pricePerItem,
          totalPrice: item.price,
          totalProfit: item.itemProfit,
          profitMargin: item.profitMargin,
          action: item.productId,
        }));
        setRows(itemRows);
      }
      if (paymentsResult && paymentsResult !== "Something went wrong...") {
        setExistingPayments(paymentsResult);
      }
    } catch (error) {
      console.error("Error fetching warehouse transfer bill:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSave = async () => {
    try {
      const submitResult = await updateSaleBillPayments(billId, newPayments);
      if (submitResult !== "Something went wrong...") {
        setEnablePayment(false);
        setNewPayments([]);
        fetchData();
      } else {
        setPaymentAlert({
          show: true,
          type: "error",
          message: "Failed to update payments.",
        });
      }
    } catch (error) {
      setPaymentAlert({
        show: true,
        type: "error",
        message: "Error saving payments.",
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [billId]);

  const columns = [
    {
      id: "productName",
      label: "Product Name",
      minWidth: 150,
      editable: false,
    },
    { id: "barcode", label: "Barcode", minWidth: 120, editable: false },
    {
      id: "sellingQty",
      label: "Qty",
      minWidth: 80,
      editable: false,
      type: "number",
      isDecimal: false,
    },
    {
      id: "unitPrice",
      label: "Unit Price",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      id: "totalPrice",
      label: "Total Price",
      minWidth: 120,
      editable: false,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      id: "totalProfit",
      label: "Profit",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      id: "profitMargin",
      label: "Margin",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: false,
    },
    { id: "action", label: "Actions", minWidth: 80 },
  ];

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setOpenItemView(true);
  };

  const handleCloseItemView = () => {
    setOpenItemView(false);
    setSelectedItem(null);
  };

  const handleAction = (event, productId) => {
    const item = transferItems.find((i) => i.productId === productId);
    if (item) handleViewItem(item);
  };

  const showAlert = (type, message) => {
    setAlertData({ type, message });
    setDisplayAlert(true);
  };

  const handleSendForApproval = async () => {
    setActionLoading(true);
    try {
      await sendWarehouseTransferBillForApproval(billId);
      fetchData();
    } catch (error) {
      showAlert("error", "Failed to send for approval.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveBill = async () => {
    setActionLoading(true);
    try {
      const result = await approveWarehouseTransferBill(billId);
      if (result !== "Something went wrong...") {
        showAlert("success", "Bill approved. Status changed to APPROVED.");
        fetchData();
      } else {
        showAlert("error", "Failed to approve the bill.");
      }
    } catch (error) {
      showAlert("error", "Error approving bill.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectBill = async () => {
    setActionLoading(true);
    try {
      const result = await rejectWarehouseTransferBill(billId);
      if (result !== "Something went wrong...") {
        showAlert("success", "Bill rejected. Status changed to REJECTED.");
        fetchData();
      } else {
        showAlert("error", "Failed to reject the bill.");
      }
    } catch (error) {
      showAlert("error", "Error rejecting bill.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferItems = async () => {
    setActionLoading(true);
    try {
      const result = await executeWarehouseTransfer(billId);
      if (result !== "Something went wrong...") {
        showAlert(
          "success",
          "Items dispatched from source warehouse. Status changed to TRANSFERRED.",
        );
        fetchData();
      } else {
        showAlert(
          "error",
          "Failed to execute transfer. Check inventory levels.",
        );
      }
    } catch (error) {
      showAlert("error", "Error executing transfer.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmReceived = async () => {
    setActionLoading(true);
    try {
      const result = await confirmWarehouseTransferReceived(billId);
      if (result !== "Something went wrong...") {
        showAlert(
          "success",
          "Receipt confirmed. Inventory added to destination warehouse. Status changed to RECEIVED.",
        );
        fetchData();
      } else {
        showAlert("error", "Failed to confirm receipt.");
      }
    } catch (error) {
      showAlert("error", "Error confirming receipt.");
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`;

  const getStatusChip = (status) => {
    const map = {
      DRAFT: {
        color: theme.palette.warning.main,
        bg: alpha(theme.palette.warning.main, 0.1),
      },
      AWAITING: {
        color: theme.palette.info.main,
        bg: alpha(theme.palette.info.main, 0.1),
      },
      APPROVED: {
        color: theme.palette.success.main,
        bg: alpha(theme.palette.success.main, 0.1),
      },
      COMPLETED: {
        color: theme.palette.success.dark,
        bg: alpha(theme.palette.success.dark, 0.1),
      },
      REJECTED: {
        color: theme.palette.error.main,
        bg: alpha(theme.palette.error.main, 0.1),
      },
    };
    const style = map[status?.toUpperCase()] || {
      color: theme.palette.grey[600],
      bg: alpha(theme.palette.grey[600], 0.1),
    };
    return (
      <Chip
        label={status || "Unknown"}
        size="small"
        sx={{
          bgcolor: style.bg,
          color: style.color,
          fontWeight: 600,
          borderRadius: 1,
          px: 1,
        }}
      />
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Loading bill details...</Typography>
      </Box>
    );
  }

  if (!bill) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <DescriptionIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Bill Not Found
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  // Construct warehouse objects for the WarehouseTransferBillWarehouseView component
  const sourceWarehouseObj = {
    warehouseName: bill.sourceWarehouseName,
    warehouseType: null,
  };
  const destinationWarehouseObj = {
    warehouseName: bill.destinationWarehouseName,
    warehouseType: null,
  };

  return (
    <Paper
      elevation={3}
      sx={{ p: { xs: 2, md: 2 }, m: { xs: 1, md: 2 }, borderRadius: 2 }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ReceiptIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary.main">
              Warehouse Transfer Bill
            </Typography>
            {bill.billCategory && (
              <Chip
                label={bill.billCategory}
                size="small"
                color={bill.billCategory === "RETAIL" ? "primary" : "secondary"}
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.2) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Bill Info + Warehouse Transfer */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TagIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Bill Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Bill Number
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {bill.billNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {bill.transferDate
                        ? new Date(bill.transferDate).toLocaleDateString()
                        : "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Typography variant="body1">{bill.addedBy}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  {getStatusChip(bill.status)}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <LocalShippingIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Warehouse Transfer
                </Typography>
              </Box>
              <WarehouseTransferBillWarehouseView
                selectedSource={sourceWarehouseObj}
                selectedDestination={destinationWarehouseObj}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Summary */}
      <Card elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AttachMoneyIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Financial Summary
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography variant="h6" fontWeight="700" color="success.main">
                  {formatCurrency(bill.totalPrice)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Profit
                </Typography>
                <Typography variant="h6" fontWeight="700" color="primary.main">
                  {formatCurrency(bill.totalProfit)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Discount
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <DiscountIcon sx={{ fontSize: 16, color: "warning.main" }} />
                  <Typography variant="body1" fontWeight="500">
                    {bill.discountPercentage > 0
                      ? `${bill.discountPercentage}%`
                      : formatCurrency(bill.discountAmount)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Remaining Amount
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  color={
                    parseFloat(bill.remainingAmount) > 0
                      ? "warning.main"
                      : "text.primary"
                  }
                >
                  {formatCurrency(bill.remainingAmount)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Note */}
      {bill.note && (
        <Card elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <DescriptionIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Note
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ pl: 3, mt: 1 }}>
              {bill.note}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Bill Items */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <StorefrontIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Transfer Items
            </Typography>
            <Badge badgeContent={rows.length} color="primary" sx={{ ml: 1 }} />
          </Box>
          <Box
            sx={{
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <TableItem
              columns={columns}
              rows={rows}
              page={0}
              setPage={() => {}}
              tablePagin={false}
              totalPages={1}
              getData={fetchData}
              showActions={{ view: "Bill item view" }}
              actionRoles={{ view: ["WT_BILL_VIEW", "ROLE_ADMIN"] }}
              handleAction={handleAction}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Payments */}
      <Card elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PaymentIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Payments
            </Typography>
          </Box>

          {existingPayments.length > 0 && !enablePayment && (
            <PaymentListForConfirmation
              payments={existingPayments.map((p) => ({
                ...p,
                type: p.paymentType,
              }))}
            />
          )}

          {existingPayments.length === 0 && !enablePayment && (
            <Typography color="text.secondary" variant="body2">
              No payments recorded yet.
            </Typography>
          )}

          {enablePayment && (
            <>
              <PaymentOptions
                paymentMethods={newPayments}
                setPaymentMethods={setNewPayments}
                billType={"RETAIL"}
                paymentTypes={[
                  { value: "CASH", label: "Cash" },
                  { value: "CHEQUE", label: "Cheque" },
                  { value: "BILL", label: "Recover Import Bill" },
                  { value: "RECEIPT", label: "Receipt" },
                ]}
              />
              {paymentAlert.show && (
                <Alert
                  severity={paymentAlert.type}
                  onClose={() =>
                    setPaymentAlert({ show: false, type: "", message: "" })
                  }
                  sx={{ mt: 2 }}
                >
                  {paymentAlert.message}
                </Alert>
              )}
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setEnablePayment(false);
                    setNewPayments([]);
                    setPaymentAlert({ show: false, type: "", message: "" });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PaymentIcon />}
                  onClick={handlePaymentSave}
                  disabled={newPayments.length === 0}
                >
                  Save Payments
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Alert */}
      {displayAlert && (
        <Box sx={{ mb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor:
                alertData.type === "success"
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.error.main, 0.1),
              border: `1px solid ${
                alertData.type === "success"
                  ? theme.palette.success.main
                  : theme.palette.error.main
              }`,
            }}
          >
            <Typography
              variant="body2"
              color={
                alertData.type === "success" ? "success.main" : "error.main"
              }
              fontWeight="500"
            >
              {alertData.message}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>

        {/* Payment — visible when bill has a remaining balance and is past DRAFT/AWAITING */}
        {!["DRAFT", "AWAITING", "REJECTED"].includes(bill.status) &&
          parseFloat(bill.remainingAmount) > 0 && (
            <RoleBasedRender
              allowedRoles={["WT_BILL_PAYMENT_UPDATE", "ROLE_ADMIN"]}
            >
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PaymentIcon />}
                onClick={() => {
                  const converted = existingPayments.map((p) => ({
                    type: p.paymentType,
                    amount: p.amount,
                    bank: p.bank,
                    chequeNumber: p.chequeNumber,
                    returnDate: p.returnDate,
                    returnDateInput:
                      p.returnDate != null ? dayjs(p.returnDate) : null,
                    receiptNumber: p.receiptNumber,
                    shopName: p.shopName,
                    returnBillNumber: p.returnBillNumber,
                  }));
                  setNewPayments(converted);
                  setEnablePayment(true);
                }}
                sx={{ px: 3 }}
              >
                Add Payment
              </Button>
            </RoleBasedRender>
          )}

        {/* DRAFT → send for approval */}
        {bill.status === "DRAFT" && (
          <Button
            onClick={handleSendForApproval}
            variant="contained"
            color="primary"
            disabled={actionLoading}
            startIcon={<SendIcon />}
            sx={{ px: 3 }}
          >
            Send for Approval
          </Button>
        )}

        {/* AWAITING → approve or reject */}
        {bill.status === "AWAITING" && (
          <>
            <RoleBasedRender allowedRoles={["WT_BILL_APPROVE", "ROLE_ADMIN"]}>
              <Button
                onClick={handleRejectBill}
                variant="outlined"
                color="error"
                disabled={actionLoading}
                startIcon={<CancelIcon />}
                sx={{ px: 3 }}
              >
                {actionLoading ? "Processing..." : "Reject"}
              </Button>
              <Button
                onClick={handleApproveBill}
                variant="contained"
                color="success"
                disabled={actionLoading}
                startIcon={<CheckCircleIcon />}
                sx={{ px: 3 }}
              >
                {actionLoading ? "Processing..." : "Approve"}
              </Button>
            </RoleBasedRender>
          </>
        )}

        {/* APPROVED → source warehouse dispatches goods */}
        {bill.status === "APPROVED" && (
          <Button
            onClick={handleTransferItems}
            variant="contained"
            color="warning"
            disabled={actionLoading}
            startIcon={<LocalShippingIcon />}
            sx={{ px: 3 }}
          >
            {actionLoading ? "Processing..." : "Transfer Items"}
          </Button>
        )}

        {/* TRANSFERRED → destination warehouse confirms receipt */}
        {bill.status === "TRANSFERRED" && (
          <Button
            onClick={handleConfirmReceived}
            variant="contained"
            color="success"
            disabled={actionLoading}
            startIcon={<CheckCircleIcon />}
            sx={{ px: 3 }}
          >
            {actionLoading ? "Processing..." : "Confirm Received"}
          </Button>
        )}
      </Box>

      {/* Item Details Dialog */}
      <BillItemDetailsDialog
        open={openItemView}
        onClose={handleCloseItemView}
        item={selectedItem}
        billCategory={bill?.billCategory}
      />
    </Paper>
  );
}

WarehouseTransferBillView.propTypes = {
  billId: PropTypes.string,
};

export default WarehouseTransferBillView;
